/**
 * useAppUpdate — ONE update lifecycle, and it says where it is.
 *
 * Tom, 2026-09-22: "why do we often need to crash the PWA when updating… The
 * app should just do what it says it is doing, with better progress markers of
 * where it is in the process."
 *
 * WHY IT USED TO NEED A FORCE-QUIT (job #759, diagnosed from the code):
 *
 *   1. The app never activated the waiting service worker. "Update" was a plain
 *      reload through the OLD worker, and the new one was left to take over
 *      "the first time no client is open". On an installed iPhone PWA the
 *      document is suspended, not closed, for days — so "no client open" meant
 *      exactly one thing: the learner force-quitting the app.
 *   2. That reload went NetworkFirst with a 3s timeout, and the timeout's
 *      fallback is the precached shell — the OLD worker's precache, the OLD
 *      index.html. A phone waking from the background on a sleepy network
 *      routinely misses 3s, so "Update" landed back on the old build, the
 *      one-attempt guard refused a second try and the banner came back. Only
 *      the force-quit, which let the waiting worker activate and swap the
 *      precache, ever finished the job — Tom's "then you reopen it and it's
 *      finished".
 *   3. Nothing checked on resume. The on-open gate ran once per document and
 *      the 5-minute poll is a setInterval iOS freezes in the background.
 *   4. Settings' "update to latest" deleted the Workbox precache outright — the
 *      waiting worker's freshly downloaded files with it — so offline broke for
 *      the rest of that build's life.
 *
 * THE CURE, in one line: download first, activate the new worker, THEN reload.
 * Once the new worker is active its precache IS the new build, so the reload
 * lands on the new build whether the network answers in 30ms, in 30s or not at
 * all. The historical danger with activating — the old document losing its own
 * chunks from the precache and dying at its next lazy import — cannot bite,
 * because the document is gone a heartbeat later and nothing in between loads
 * code: the only thing on screen is the update line, already rendered.
 *
 * THE STAGES the learner sees, each one true when it shows:
 *   checking    → only when they asked ("Checking for a new version")
 *   found       → a newer build is live
 *   downloading → n of m files, counted in the precache itself against the new
 *                 build's own manifest (/sw-manifest.json), m being only the
 *                 files the phone does not already hold; uncounted, and said
 *                 so, when the manifest cannot be read
 *   ready       → the new worker is installed and waiting
 *   restarting  → it is being activated and the page is reloading
 *   wedged      → the reload did not take (iOS standalone); a tap finishes it
 *   updated     → after the reload, naming the build now running
 *   failed      → with the reason and a retry
 *
 * NEVER OVER A LESSON. Nothing here restarts while anything is sounding (Tom's
 * standing rule since 2026-05-21). Found in the middle of play, the new version
 * downloads quietly and waits; the restart is offered at the next pause, and
 * the pagehide flush in LearningPlayer saves the exact cycle, so the learner
 * comes back where they were.
 */
import { ref, computed } from 'vue'
import { fetchLatestBuild, updateAvailable, userDismissed, setApplyUpdate } from './usePwaUpdate'
import type { BuildStamp } from '../platform/buildStaleness'
import { shouldRunServiceWorker } from '../platform/capabilities'
import { RELOAD_WEDGE_MS } from '../utils/bootHeal'
import { registerServiceWorker } from '../platform/serviceWorker'
import { inNoReloadZone } from '../platform/noReloadZone'

export type UpdatePhase =
  | 'idle' | 'checking' | 'current' | 'found' | 'downloading'
  | 'ready' | 'restarting' | 'wedged' | 'updated' | 'failed'

export type UpdateTrigger = 'open' | 'resume' | 'poll' | 'manual' | 'other-window'

export type UpdateFailure =
  | 'offline'    // could not reach the deployment at all
  | 'download'   // the new worker failed to install
  | 'stalled'    // no file arrived for STALL_MS, on an install whose files are counted
  | 'still-old'  // restarted, and came back on the old build anyway

/** No new file for this long while downloading = stalled, said out loud.
 *  Only where files ARE counted (job #222): with no manifest to count
 *  against there is nothing to measure, so a healthy install used to be
 *  called stalled at the first tick past this, every time. Uncounted, the
 *  worker's own state is the verdict — installed, or redundant. */
export const STALL_MS = 45_000
/** How long activation may take before we reload regardless. */
export const ACTIVATE_TIMEOUT_MS = 10_000
/** A version answer slower than this is no answer. */
export const CHECK_TIMEOUT_MS = 5_000
/** How often the download count is re-read. */
export const PROGRESS_POLL_MS = 400
/** "Ready — restarting" stays up this long before the page goes, so it is read. */
export const RESTART_BEAT_MS = 600
/** How long "Updated to …" / "You have the latest" stay up. */
export const NOTICE_MS = 6_000
/** Background re-check while the app is open and visible. */
export const POLL_MS = 5 * 60 * 1000
/** Resuming after being hidden at least this long counts as a check. */
export const RESUME_MIN_HIDDEN_MS = 15_000
/** The before-reload record; read once by the next document. */
export const RECORD_KEY = 'ssi-app-update-record'
/** A record older than this is not about this reload. */
export const RECORD_TTL_MS = 5 * 60 * 1000

export const updatePhase = ref<UpdatePhase>('idle')
export const updateProgress = ref<{ done: number; total: number } | null>(null)
/** The build being updated TO; after reload, the build now running. */
export const updateTarget = ref<string | null>(null)
export const updateFailure = ref<UpdateFailure | null>(null)
export const updateTrigger = ref<UpdateTrigger | null>(null)

/** Something is in motion that the learner should not interrupt. */
export const updateBusy = computed(() =>
  ['checking', 'found', 'downloading', 'restarting', 'wedged'].includes(updatePhase.value))

export interface UpdateDeps {
  running: string
  fetchLatest: () => Promise<BuildStamp | null>
  getRegistration: () => Promise<ServiceWorkerRegistration | null>
  /** The new build's precache keys, read from /sw-manifest.json. */
  fetchManifest: () => Promise<{ buildNumber: string | null; keys: string[] } | null>
  /** How many of those keys are already in the precache. */
  countCached: (keys: string[]) => Promise<number>
  isPlaying: () => boolean
  /** On a page where the app never reloads itself (platform/noReloadZone). */
  isWorking: () => boolean
  reload: () => void
  now: () => number
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
}

// @ts-ignore - __BUILD_NUMBER__ is defined by Vite
const RUNNING_BUILD: string = typeof __BUILD_NUMBER__ !== 'undefined' ? __BUILD_NUMBER__ : 'dev'

let audible = false
let playStateBound = false
function bindPlayState(): void {
  if (playStateBound || typeof window === 'undefined') return
  playStateBound = true
  window.addEventListener('ssi-play-state', (e: Event) => {
    audible = !!(e as CustomEvent).detail?.playing
  })
}

function safeSession(): UpdateDeps['storage'] {
  try { return typeof sessionStorage !== 'undefined' ? sessionStorage : null } catch { return null }
}

async function defaultRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null
  if (!shouldRunServiceWorker()) return null
  try { return (await navigator.serviceWorker.getRegistration()) ?? null } catch { return null }
}

async function defaultManifest(): Promise<{ buildNumber: string | null; keys: string[] } | null> {
  try {
    const res = await fetch(`/sw-manifest.json?t=${Date.now()}`, { cache: 'no-store' })
    if (!res.ok) return null
    const body = await res.json()
    if (!Array.isArray(body?.entries)) return null
    const keys = body.entries
      .filter((e: unknown): e is { url: string; revision: string | null } =>
        !!e && typeof (e as { url?: unknown }).url === 'string')
      .map((e: { url: string; revision: string | null }) => precacheKey(e.url, e.revision))
    return { buildNumber: typeof body.buildNumber === 'string' ? body.buildNumber : null, keys }
  } catch {
    return null
  }
}

/** Workbox's own cache key for a manifest entry (workbox-precaching createCacheKey). */
export function precacheKey(url: string, revision: string | null, base = typeof location !== 'undefined' ? location.origin + '/' : 'http://localhost/'): string {
  const u = new URL(url, base)
  if (revision) u.searchParams.set('__WB_REVISION__', revision)
  return u.href
}

async function defaultCountCached(keys: string[]): Promise<number> {
  if (typeof caches === 'undefined') return 0
  const names = await caches.keys()
  const name = names.find((n) => n.indexOf('workbox-precache') === 0)
  if (!name) return 0
  const cached = new Set((await (await caches.open(name)).keys()).map((r) => r.url))
  let n = 0
  for (const k of keys) if (cached.has(k)) n++
  return n
}

function defaultDeps(): UpdateDeps {
  return {
    running: RUNNING_BUILD,
    fetchLatest: fetchLatestBuild,
    getRegistration: defaultRegistration,
    fetchManifest: defaultManifest,
    countCached: defaultCountCached,
    isPlaying: () => audible,
    isWorking: () => inNoReloadZone(),
    reload: () => window.location.reload(),
    now: () => Date.now(),
    storage: safeSession(),
  }
}

let deps: UpdateDeps = defaultDeps()
/** Apply as soon as ready: the learner asked, or the open screen is holding. */
let autoApply = false
let selfActivating = false
let noticeTimer: ReturnType<typeof setTimeout> | null = null
let wedgeTimer: ReturnType<typeof setTimeout> | null = null
let runId = 0

/** Test seam. */
export function __setUpdateDepsForTest(overrides: Partial<UpdateDeps>): void {
  deps = { ...defaultDeps(), ...overrides }
}
export function __resetUpdateForTest(): void {
  deps = defaultDeps()
  autoApply = false
  selfActivating = false
  audible = false
  runId++
  if (noticeTimer) clearTimeout(noticeTimer)
  if (wedgeTimer) clearTimeout(wedgeTimer)
  noticeTimer = wedgeTimer = null
  updatePhase.value = 'idle'
  updateProgress.value = null
  updateTarget.value = null
  updateFailure.value = null
  updateTrigger.value = null
  updateAvailable.value = false
  userDismissed.value = false
}

function setPhase(p: UpdatePhase): void {
  updatePhase.value = p
  // The blue dot and the Settings badge read this shared flag.
  updateAvailable.value = p === 'ready'
  if (noticeTimer) { clearTimeout(noticeTimer); noticeTimer = null }
  if (p === 'updated' || p === 'current') {
    noticeTimer = setTimeout(() => {
      if (updatePhase.value === p) setPhase('idle')
    }, NOTICE_MS)
  }
}

function fail(reason: UpdateFailure): void {
  updateFailure.value = reason
  autoApply = false
  userDismissed.value = false
  setPhase('failed')
}

async function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      p.catch(() => null),
      new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), ms) }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

/**
 * Ask the deployment what is live and, if it is not what is running, take it
 * through the stages. `auto` applies the moment it is ready (and nothing is
 * sounding); otherwise the banner offers the restart.
 */
export async function checkForUpdate(trigger: UpdateTrigger, opts: { auto?: boolean; latest?: BuildStamp | null } = {}): Promise<UpdatePhase> {
  if (updateBusy.value) return updatePhase.value
  // Coming back from the background is the safe moment (job #806). An installed
  // phone PWA is suspended, not closed, for days, so the open gate never runs
  // again and a banner nobody taps left devices on old builds until ?reset=1.
  // Resume applies by itself unless something is sounding — markReady and the
  // ready branch below both refuse while audible, and the banner takes over at
  // the pause. Nothing else (poll, online) applies unasked: the learner is
  // looking at the screen then.
  const auto = !!opts.auto || trigger === 'resume'
  if (updatePhase.value === 'ready') {
    if (trigger === 'manual' ? !deps.isPlaying() : auto && !deps.isPlaying() && !deps.isWorking()) await applyUpdate()
    return updatePhase.value
  }
  const manual = trigger === 'manual'
  if (manual) { updateFailure.value = null; setPhase('checking') }

  const latest = opts.latest !== undefined ? opts.latest : await withTimeout(deps.fetchLatest(), CHECK_TIMEOUT_MS)
  const live = latest?.buildNumber ?? null
  if (!live) {
    if (manual) fail('offline')
    return updatePhase.value
  }
  if (live === deps.running) {
    if (manual) { updateTarget.value = live; setPhase('current') }
    void settleWaitingWorker()
    return updatePhase.value
  }
  // A failure the learner has not retried stays on screen; a background check
  // never paints over it.
  if (updatePhase.value === 'failed' && !manual && !auto) return updatePhase.value

  updateTarget.value = live
  updateTrigger.value = trigger
  updateFailure.value = null
  userDismissed.value = false
  autoApply = manual || auto
  setPhase('found')
  await download()
  return updatePhase.value
}

async function download(): Promise<void> {
  const myRun = ++runId
  const reg = await deps.getRegistration()
  if (myRun !== runId) return
  if (!reg) {
    // No service worker here (unsupported, or a shell that does not run one):
    // there is nothing to download ahead — the reload fetches the new build.
    return markReady()
  }

  setPhase('downloading')
  updateProgress.value = null
  const manifest = await deps.fetchManifest()
  // Count only against the manifest of the build we are going to. A deploy
  // landing in between would make the count a lie, so it goes uncounted.
  const keys = manifest && (!manifest.buildNumber || manifest.buildNumber === updateTarget.value) && manifest.keys.length
    ? manifest.keys : null
  // Count only what this update FETCHES (job #966). Workbox skips every cache
  // key the precache already holds, and a file whose content did not change
  // keeps its key from build to build, so those were never downloading. Counting
  // them made every update read "of 207" when a third of that came down.
  let already: number | null = null
  const readCount = async () => {
    if (!keys) return
    try {
      const cachedNow = Math.min(await deps.countCached(keys), keys.length)
      if (already === null) already = cachedNow
      const total = keys.length - already
      updateProgress.value = { done: Math.max(0, Math.min(cachedNow - already, total)), total }
    } catch { /* keep the last honest count */ }
  }
  await readCount()

  try {
    await reg.update()
  } catch {
    if (myRun === runId) fail('offline')
    return
  }
  if (myRun !== runId) return

  const worker = reg.installing || reg.waiting
  if (!worker || worker.state === 'installed' || worker.state === 'activated' || worker.state === 'activating') {
    await readCount()
    return markReady()
  }

  // Watch it install, re-reading the count, until it lands, dies or stalls.
  let lastDone = updateProgress.value?.done ?? -1
  let lastMoved = deps.now()
  let settled: 'installed' | 'redundant' | null = null
  const onState = () => {
    if (worker.state === 'installed' || worker.state === 'activating' || worker.state === 'activated') settled = 'installed'
    else if (worker.state === 'redundant') settled = 'redundant'
  }
  worker.addEventListener('statechange', onState)
  try {
    while (!settled) {
      await sleep(PROGRESS_POLL_MS)
      if (myRun !== runId) return
      onState()
      await readCount()
      const done = updateProgress.value?.done ?? -1
      if (done !== lastDone) { lastDone = done; lastMoved = deps.now() }
      if (!settled && keys && deps.now() - lastMoved > STALL_MS) {
        fail('stalled')
        return
      }
    }
  } finally {
    worker.removeEventListener('statechange', onState)
  }
  if (settled === 'redundant') {
    // A newer worker may have superseded this one; that is not a failure.
    if (reg.waiting || reg.installing) return void download()
    return fail('download')
  }
  await readCount()
  markReady()
}

function markReady(): void {
  if (updateProgress.value) updateProgress.value = { done: updateProgress.value.total, total: updateProgress.value.total }
  setPhase('ready')
  if (autoApply && !deps.isPlaying() && !deps.isWorking()) void applyUpdate()
}

/** Until the worker is installed (or dies), bounded by STALL_MS. */
function waitForInstalled(worker: ServiceWorker): Promise<void> {
  return new Promise((resolve) => {
    const settled = () => worker.state !== 'installing' && worker.state !== 'parsed'
    if (settled()) return resolve()
    const done = () => { clearTimeout(t); worker.removeEventListener('statechange', onState); resolve() }
    const onState = () => { if (settled()) done() }
    const t = setTimeout(done, STALL_MS)
    worker.addEventListener('statechange', onState)
  })
}

function waitForActivation(worker: ServiceWorker): Promise<void> {
  return new Promise((resolve) => {
    if (worker.state === 'activated') return resolve()
    const done = () => { clearTimeout(t); worker.removeEventListener('statechange', onState); resolve() }
    const onState = () => { if (worker.state === 'activated' || worker.state === 'redundant') done() }
    const t = setTimeout(done, ACTIVATE_TIMEOUT_MS)
    worker.addEventListener('statechange', onState)
  })
}

/**
 * Take the update: activate the waiting worker, wait for it to finish
 * activating (its precache cleanup runs inside `activate`, so only then is the
 * precache purely the new build), then reload. Wired to the banner's
 * "Restart now", the logo dot, and the open screen.
 */
export async function applyUpdate(): Promise<void> {
  if (updatePhase.value === 'restarting' || updatePhase.value === 'wedged') return
  setPhase('restarting')
  try {
    deps.storage?.setItem(RECORD_KEY, JSON.stringify({ from: deps.running, to: updateTarget.value, at: deps.now() }))
  } catch { /* storage blocked: we lose the confirmation line, not the update */ }

  const reg = await deps.getRegistration()
  const waiting = reg?.waiting
  if (waiting) {
    selfActivating = true
    const activated = waitForActivation(waiting)
    waiting.postMessage({ type: 'SKIP_WAITING' })
    await activated
  }
  await sleep(RESTART_BEAT_MS)

  // A programmatic reload in an iOS standalone webview can silently not take.
  // If we are still here after the same wedge window the boot watchdog uses,
  // say so and hand over a button: a tapped reload is always honoured, and it
  // lands on the new build because the new worker is already in charge.
  if (wedgeTimer) clearTimeout(wedgeTimer)
  wedgeTimer = setTimeout(() => {
    if (updatePhase.value === 'restarting') setPhase('wedged')
  }, RELOAD_WEDGE_MS)
  deps.reload()
}

/** "Tap to finish restarting" — a reload inside a real user gesture. */
export function finishRestart(): void {
  deps.reload()
}

/** "Later" / "Continue on this version". */
export function dismissUpdate(): void {
  if (updatePhase.value === 'ready') { userDismissed.value = true; return }
  if (updatePhase.value === 'failed' || updatePhase.value === 'current' || updatePhase.value === 'updated') setPhase('idle')
}

/** Retry after a failure — always an explicit, learner-asked attempt. */
export function retryUpdate(): Promise<UpdatePhase> {
  setPhase('idle')
  return checkForUpdate('manual')
}

/**
 * The page is already running the live build but an older worker still
 * controls it, with the live build's worker waiting behind it. That happens
 * on every cold launch whose network answered in time: the fresh index.html
 * came from the network, so the learner is on the new build, but offline
 * they would still boot the old precache. Hand over to the waiting worker —
 * safe here, because its precache holds exactly the chunks this page runs,
 * so the cleanup that runs on activation removes only the old build's files.
 * `update()` first, so we only ever activate the worker the origin serves
 * now; if a newer one is still installing, a later check settles it.
 * Never while anything is playing.
 */
export async function settleWaitingWorker(): Promise<boolean> {
  if (deps.isPlaying()) return false
  const reg = await deps.getRegistration()
  if (!reg) return false
  // A cold launch usually finds the live build's worker still INSTALLING —
  // the browser fetched the new sw.js on this very navigation. Wait for it.
  if (reg.installing) await waitForInstalled(reg.installing)
  if (!reg.waiting) return false
  try { await reg.update() } catch { return false }
  if (reg.installing) await waitForInstalled(reg.installing)
  if (reg.installing || !reg.waiting || deps.isPlaying()) return false
  selfActivating = true
  reg.waiting.postMessage({ type: 'SKIP_WAITING' })
  return true
}

/**
 * Read what the previous document wrote just before it reloaded, and say how
 * it went: "Updated to <build>" or, honestly, that it came back on the old one.
 *
 * `previousBuild` is the build this device last ran (App.vue's
 * ssi-build-version, read before it is overwritten). With no record — the app
 * was closed and simply opened onto a newer build — a change from it is still
 * said out loud, so every path that updates also says it is done.
 */
export function confirmAfterReload(previousBuild: string | null = null): UpdatePhase {
  let raw: string | null = null
  try { raw = deps.storage?.getItem(RECORD_KEY) ?? null; deps.storage?.removeItem(RECORD_KEY) } catch { /* blocked */ }
  if (!raw) {
    if (previousBuild && previousBuild !== deps.running && deps.running !== 'dev') {
      updateTarget.value = deps.running
      setPhase('updated')
    }
    return updatePhase.value
  }
  let rec: { from?: string; to?: string | null; at?: number } | null = null
  try { rec = JSON.parse(raw) } catch { return updatePhase.value }
  if (!rec || typeof rec.at !== 'number' || deps.now() - rec.at > RECORD_TTL_MS) return updatePhase.value
  if ((rec.to && rec.to === deps.running) || (!rec.to && rec.from !== deps.running)) {
    updateTarget.value = deps.running
    setPhase('updated')
  } else if (rec.from === deps.running) {
    updateTarget.value = rec.to ?? null
    fail('still-old')
  }
  return updatePhase.value
}

let watching = false
let hiddenAt: number | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null

/**
 * Register the service worker and check for updates at every moment a learner
 * could be looking at an old build: resuming from the background, coming back
 * online, and every POLL_MS while visible. Launch is the open gate's job
 * (useOpenUpdateGate), which calls checkForUpdate itself.
 */
export function startUpdateWatch(): void {
  if (watching || typeof window === 'undefined') return
  watching = true
  bindPlayState()
  setApplyUpdate(() => { void applyUpdate() })

  if (import.meta.env.PROD && registerServiceWorker()) {
    const hadController = !!navigator.serviceWorker.controller
    // Another window took the update. This document is now running under a
    // worker whose precache no longer holds its code, so it must restart too —
    // at the next pause, offered, never forced.
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (selfActivating || !hadController) return
      if (updatePhase.value === 'restarting' || updatePhase.value === 'wedged') return
      updateTrigger.value = 'other-window'
      autoApply = false
      userDismissed.value = false
      setPhase('ready')
    })
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return }
    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt
    hiddenAt = null
    if (away >= RESUME_MIN_HIDDEN_MS) void checkForUpdate('resume')
  })
  // iOS restores a standalone PWA from the back-forward cache without a
  // visibilitychange on some versions; pageshow(persisted) covers it.
  window.addEventListener('pageshow', (e) => {
    if ((e as PageTransitionEvent).persisted) void checkForUpdate('resume')
  })
  window.addEventListener('online', () => { void checkForUpdate('poll') })
  pollTimer = setInterval(() => {
    if (document.visibilityState === 'visible') void checkForUpdate('poll')
  }, POLL_MS)
}

/** Test seam. */
export function __stopUpdateWatchForTest(): void {
  if (pollTimer) clearInterval(pollTimer)
  pollTimer = null
  watching = false
}
